import {
  DeliveryDetails,
  DeliveryProduct,
  DeliveryProviderQuote,
  LogisticProviderConfig,
  OrderCalculation,
} from "../entities/delivery.entities";

export const parseRawProducts = (
  products: DeliveryProduct[] | string | undefined
): DeliveryProduct[] => {
  if (!products) return [];
  if (Array.isArray(products)) return products;
  if (typeof products !== "string" || !products.trim()) return [];
  try {
    const parsed = JSON.parse(products) as unknown;
    return Array.isArray(parsed) ? (parsed as DeliveryProduct[]) : [];
  } catch {
    return [];
  }
};

export const normalizeDeliveryDetailRaw = (
  detail: DeliveryDetails
): DeliveryDetails => {
  if (!detail.raw) return detail;

  const products = parseRawProducts(detail.raw.products);

  return {
    ...detail,
    raw: {
      ...detail.raw,
      products,
    },
    deliverySize: detail.raw.deliverySize ?? detail.deliverySize,
    pickupDate: detail.raw.pickupDate ?? detail.pickupDate,
    products: products.length > 0 ? products : detail.products,
  };
};

export const getDeliveryDisplayId = (detail: DeliveryDetails): number | string =>
  detail.id ?? detail.orderId ?? "—";

export const resolveDetailLinkedOrderId = (
  detail: DeliveryDetails
): number | null => {
  if (detail.linkedOrderId) return detail.linkedOrderId;
  if (detail.isFromOrder && detail.orderId) return detail.orderId;
  return null;
};

const emptyCustomer = () => ({
  city: "",
  address: "",
  lat: 0,
  lng: 0,
  addressDetails: "",
  name: "",
  phone: "",
  prefixPhone: "",
  email: "",
  observations: "",
  postalCode: "",
});

const normalizeCustomer = (detail: DeliveryDetails) => {
  const user = detail.customer;
  if (!user) return emptyCustomer();
  return {
    city: user.city ?? "",
    address: user.address ?? "",
    lat: user.lat ?? 0,
    lng: user.lng ?? 0,
    addressDetails: user.addressDetails ?? "",
    name: user.name ?? "",
    phone: user.phone ?? "",
    prefixPhone: user.prefixPhone ?? "",
    email: user.email ?? "",
    observations: user.observations ?? "",
    postalCode: user.postalCode ?? "",
  };
};

export const mapDeliveryDetailToOrderCalculation = (
  detail: DeliveryDetails,
  orderId: number
): OrderCalculation => {
  const normalized = normalizeDeliveryDetailRaw(detail);
  const raw = normalized.raw;
  const parsedProducts = parseRawProducts(raw?.products);
  const products =
    parsedProducts.length > 0 ? parsedProducts : normalized.products ?? [];

  return {
    orderId: normalized.orderId ?? orderId,
    totalValue: normalized.totalValue ?? 0,
    user: normalized.owner ?? {},
    customer: normalizeCustomer(normalized),
    products,
    provider: normalized.provider,
    deliverySize: raw?.deliverySize ?? normalized.deliverySize,
    pickupDate: raw?.pickupDate ?? normalized.pickupDate,
    observations: normalized.observations,
  };
};

export const mapDetailToProviderQuote = (
  detail: DeliveryDetails,
  logisticProvider?: LogisticProviderConfig | null
): DeliveryProviderQuote | null => {
  const providerConfig = detail.providerDetail ?? logisticProvider;
  const providerId =
    detail.providerId ?? detail.provider ?? providerConfig?.id;

  if (!providerId && !detail.provider) return null;

  return {
    total: detail.totalValue ?? 0,
    provider: {
      id: providerId ?? "",
      name: providerConfig?.name ?? detail.provider,
      providerId: detail.providerId,
      deliveryType:
        providerConfig?.deliveryType ?? detail.deliveryType,
      descriptionTime:
        providerConfig?.descriptionTime ?? detail.providerDescriptionTime,
    },
  };
};

export const resolveDetailTypeLabel = (
  detail: DeliveryDetails,
  logisticProvider: LogisticProviderConfig | null | undefined,
  deliveryTypes: { id: string; label: string }[] | undefined
): string => {
  const deliveryTypeId =
    logisticProvider?.deliveryType ?? detail.deliveryType;
  if (!deliveryTypeId || !deliveryTypes?.length) return "";
  return (
    deliveryTypes.find((t) => t.id === deliveryTypeId)?.label ?? ""
  );
};

export const resolveDetailSizeLabel = (
  detail: DeliveryDetails,
  deliverySizes: { id: string; label: string }[] | undefined
): string => {
  const normalized = normalizeDeliveryDetailRaw(detail);
  const sizeId =
    normalized.raw?.deliverySize ?? normalized.deliverySize;
  if (!sizeId || !deliverySizes?.length) return sizeId ?? "";
  return (
    deliverySizes.find((s) => s.id === sizeId)?.label ?? sizeId
  );
};

export const findLogisticProviderForDetail = (
  detail: DeliveryDetails,
  providers: LogisticProviderConfig[]
): LogisticProviderConfig | null => {
  const providerKey = detail.provider ?? detail.providerId;
  if (!providerKey) return null;
  return (
    providers.find(
      (p) => p.id === providerKey || p.id === detail.providerId
    ) ?? null
  );
};

export const needsLogisticPayment = (detail: DeliveryDetails): boolean => {
  const state = detail.state ?? "";
  const paymentState = detail.orderLogisticPayment?.state?.toLowerCase();
  const paidStates = ["success", "authorize", "authorized"];

  if (paymentState && paidStates.includes(paymentState)) {
    return false;
  }

  if (
    state === "PENDING_PAYMENT" ||
    state === "pendingPayment" ||
    state === "pending_payment" ||
    state === "CALCULATED" ||
    state === "calculated"
  ) {
    return true;
  }

  if (paymentState === "pending") return true;

  if (paymentState && !paidStates.includes(paymentState)) {
    return true;
  }

  return detail.subState === "pending_payment";
};
